import { memo, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { analyticsActions } from '@actions';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { analyticsSurveyGroups } from '@helpers/surveyFilters';
import { AnalyticsContainer } from '@features/Analytics/styles';
import DoughnutRates from '@features/Analytics/Common/DoughnutRates';
import SentimentTrend from '@features/Analytics/Common/SentimentTrend';
import NpsChart from './NpsChart';
import FeedbackTable from './FeedbackTable';

const Feedback = memo(({ filter, filterVersion, feedbackFetchedVersion }) => {
    const language = useSelector(state => state.auth.user?.language);
    const account = useSelector(state => state.account?.account);
    const { feedbackData, feedbackLoading } = useSelector(state => state.analytics);
    const dispatch = useDispatch();

    const isCoverManagerConnected = isIntegrationConnected(account, AccIntegrationName.CoverManager);

    const currentQuery = useMemo(() => {
        return {
            locations: filter.locations.map(item => item.id),
            ...analyticsSurveyGroups(filter.surveys, { isCoverManagerConnected }),
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            grouping: filter.grouping,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }, [filter, isCoverManagerConnected]);

    useEffect(() => {
        if (filterVersion > feedbackFetchedVersion.current && filter.locations.length && filter.surveys.length) {
            dispatch(analyticsActions.getAnalyticsFeedback(currentQuery));
            feedbackFetchedVersion.current = filterVersion;
        }
    }, [filterVersion, currentQuery]);

    return (
        <AnalyticsContainer id="pl-content-analytics-feedbacks">
            <DoughnutRates data={feedbackData.overall} type="feedbacks" isLoading={feedbackLoading} />
            <SentimentTrend
                data={feedbackData.sentimentData}
                language={language}
                type="feedbacks"
                grouping={filter.grouping}
            />
            <NpsChart data={feedbackData.npsData} language={language} type="feedbacks" grouping={filter.grouping} />
            <FeedbackTable
                data={feedbackData.tableData}
                isLoading={feedbackLoading}
                filter={filter}
                isCoverManagerConnected={isCoverManagerConnected}
            />
        </AnalyticsContainer>
    );
});

Feedback.displayName = 'Feedback';

export default Feedback;
